import React, { useMemo, memo } from "react"
import Chart from "react-apexcharts"
import moment from "moment"
import { getWeekTooltipLabel, getSameWeekPreviousYear } from "utils/dateUtil"

const ServiceLevelChart = ({ data = [], previousData = [] }) => {
  const currentYear = data.length
    ? moment(data[0].date).year()
    : moment().year()

  const previousYear = previousData.length
    ? moment(previousData[0].date).year()
    : currentYear - 1

  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  const currentByWeek = useMemo(() => {
    const map = {}
    data.forEach(i => {
      map[moment(i.date).isoWeek()] = Number(i.level) || 0
    })
    return map
  }, [data])

  const prevByWeek = useMemo(() => {
    const map = {}
    previousData.forEach(i => {
      map[moment(i.date).isoWeek()] = Number(i.level) || 0
    })
    return map
  }, [previousData])

  const dateByWeek = useMemo(() => {
    const map = {}
    data.forEach(i => {
      map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY")
    })
    return map
  }, [data])

  const prevDateByWeek = useMemo(() => {
    const map = {}
    previousData.forEach(i => {
      map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY")
    })
    return map
  }, [previousData])

  const currentValues = allWeeks.map(w => currentByWeek[w] ?? null)
  const prevValues = allWeeks.map(w => prevByWeek[w] ?? null)
  const categories = allWeeks.map((_, i) => `${i + 1}`)

  const series = [
    {
      name: `Current Year (${currentYear})`,
      type: "column",
      data: currentValues,
    },
    { name: `Previous Year (${previousYear})`, type: "area", data: prevValues },
  ]

  const options = {
    chart: {
      type: "line",
      toolbar: { show: false },
      background: "transparent",
      animations: { enabled: true, easing: "easeinout", speed: 800 },
    },
    stroke: {
      width: [0, 2.5],
      curve: "smooth",
      dashArray: [0, 6],
    },
    markers: {
      size: [0, 3],
      colors: ["#fff"],
      strokeColors: ["#F87171"],
      strokeWidth: 2,
      hover: { size: 5 },
    },
    dataLabels: { enabled: false },
    fill: {
      opacity: [1, 0.2],
      type: ["solid", "gradient"],
      gradient: {
        type: "vertical",
        shadeIntensity: 0.5,
        opacityFrom: 0.25,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    colors: ["#A78BFA", "#F87171"],
    plotOptions: {
      bar: {
        columnWidth: "55%",
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    xaxis: {
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: -45,
        style: { colors: "#94a3b8", fontSize: "11px", fontWeight: 500 },
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        formatter: val =>
          val !== null && val !== undefined
            ? `${Number(val).toLocaleString("en-GB")}%`
            : "N/A",
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 16, bottom: 0, left: 8 },
    },
    tooltip: {
      shared: true,
      intersect: false,
      theme: "dark",
      style: { fontSize: "12px" },
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum = allWeeks[dataPointIndex]
          const currentDate = dateByWeek[weekNum]
          const prevDate = prevDateByWeek[weekNum]
          const resolvedPrevDate =
            prevDate ||
            (currentDate ? getSameWeekPreviousYear(currentDate) : null)
          const currentLabel = getWeekTooltipLabel(dataPointIndex, currentDate)
          const prevLabel = getWeekTooltipLabel(
            dataPointIndex,
            resolvedPrevDate
          )
          return `Current: ${currentLabel || "N/A"}<br/>Previous: ${
            prevLabel || "N/A"
          }`
        },
      },
      y: {
        formatter: val =>
          val !== null && val !== undefined
            ? `${Number(val).toLocaleString("en-GB")}%`
            : "N/A",
      },
    },
    legend: { show: false },
    states: {
      hover: { filter: { type: "lighten", value: 0.08 } },
      active: { filter: { type: "darken", value: 0.1 } },
    },
  }

  return (
    <div
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
        borderRadius: "16px",
        border: "1px solid #A78BFA22",
        boxShadow: "0 4px 24px #A78BFA14, 0 1px 4px rgba(0,0,0,0.04)",
        position: "relative",
        overflow: "hidden",
        marginBottom: "1.5rem",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: "linear-gradient(90deg, #A78BFA, #A78BFA88)",
          borderRadius: "16px 16px 0 0",
        }}
      />

      <div style={{ padding: "20px 16px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
            marginBottom: "4px",
            padding: "0 8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#A78BFA",
                boxShadow: "0 0 8px #A78BFA88",
              }}
            />
            <h4
              style={{
                margin: 0,
                fontSize: "15px",
                fontWeight: 700,
                color: "#1e293b",
              }}
            >
              Service Level
            </h4>
          </div>

          {/* Legend */}
          <div style={{ display: "flex", gap: "16px" }}>
            {[
              {
                color: "#A78BFA",
                label: `Current Year (${currentYear})`,
                dash: false,
              },
              {
                color: "#F87171",
                label: `Previous Year (${previousYear})`,
                dash: true,
              },
            ].map((l, i) => (
              <div
                key={i}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                {l.dash ? (
                  <span
                    style={{
                      width: 16,
                      height: 2,
                      borderTop: `2px dashed ${l.color}`,
                      display: "inline-block",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      width: 16,
                      height: 10,
                      background: l.color,
                      borderRadius: "2px",
                      display: "inline-block",
                    }}
                  />
                )}
                <small
                  style={{
                    color: "#1e293b",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  {l.label}
                </small>
              </div>
            ))}
          </div>
        </div>

        <Chart options={options} series={series} type="line" height={450} />
      </div>
    </div>
  )
}

export default ServiceLevelChart
